import React, { useState, useEffect, useMemo } from 'react';
import { Table, Button, Spin, Input, Tabs, Popconfirm, message, Modal, Tag, Tooltip, Space } from 'antd';
import { LoadingOutlined, SearchOutlined, PlusOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import './view.css';
import type { TableColumnsType } from 'antd';
import type { SortOrder } from 'antd/es/table/interface';
import { getGHGStandards, getGHGMethodologies, deleteGHGMethodology } from '../../api/ghg';

const { TabPane } = Tabs;

interface GHGStandard {
  id: number;
  code: string;
  full_form: string;
  description: string;
}

interface GHGMethodology {
  id: number;
  name: string;
  display_name: string;
  code?: string;
  include_in_project_screener?: 'Yes' | 'No' | null;
  market_label_conditions?: string;
  project_type?: string | null;
  registry_methodology?: string[] | null;
}

/**
 * Blank values (shown as '-' or '—') always sort to the end, in either direction.
 *
 * Antd negates whatever this returns when the column is in descend order, so a comparator
 * that is direction-aware has to pre-negate the blank cases to cancel that out -- otherwise
 * "blank last" in ascend becomes "blank first" in descend.
 */
const compareStrings = (a?: string | null, b?: string | null, sortOrder?: SortOrder) => {
  const aBlank = !a;
  const bBlank = !b;
  if (aBlank && bBlank) return 0;
  if (aBlank) return sortOrder === 'descend' ? -1 : 1;
  if (bBlank) return sortOrder === 'descend' ? 1 : -1;
  return a!.localeCompare(b!);
};

const ghgStandardColumns: TableColumnsType<GHGStandard> = [
  {
    title: 'Code',
    dataIndex: 'code',
    key: 'code',
    sorter: (a, b, sortOrder) => compareStrings(a.code, b.code, sortOrder),
  },
  {
    title: 'Full Form',
    dataIndex: 'full_form',
    key: 'full_form',
    sorter: (a, b, sortOrder) => compareStrings(a.full_form, b.full_form, sortOrder),
  },
  {
    title: 'Description',
    dataIndex: 'description',
    key: 'description',
    sorter: (a, b, sortOrder) => compareStrings(a.description, b.description, sortOrder),
  }
];

const ViewGHGMetaComponent: React.FC<{ activeTab?: string }> = ({ activeTab }) => {
  const defaultActiveKey = activeTab === 'ghg_standards' ? '2' : '1';
  const [loading, setLoading] = useState<boolean>(false);
  const [ghgStandards, setGHGStandards] = useState<GHGStandard[]>([]);
  const [ghgMethodologies, setGHGMethodologies] = useState<GHGMethodology[]>([]);
  const [searchText, setSearchText] = useState<string>('');
  const navigate = useNavigate();

  const projectTypeFilters = useMemo(
    () =>
      Array.from(new Set(ghgMethodologies.map((m) => m.project_type ?? null)))
        .sort((a, b) => (a ?? '').localeCompare(b ?? ''))
        .map((value) => ({ text: value ?? '-', value: value ?? 'null' })),
    [ghgMethodologies],
  );

  const ghgMethodologyColumns: TableColumnsType<GHGMethodology> = [
    {
      title: 'Name',
      dataIndex: 'display_name',
      key: 'name',
      sorter: (a, b, sortOrder) => compareStrings(a.display_name, b.display_name, sortOrder),
    },
    {
      title: 'Short Display Name',
      dataIndex: 'code',
      key: 'code',
      sorter: (a, b, sortOrder) => compareStrings(a.code, b.code, sortOrder),
      render: (value: string | undefined) => value || '—',
    },
    {
      title: 'Include in Project Screener',
      dataIndex: 'include_in_project_screener',
      key: 'include_in_project_screener',
      filters: [
        { text: 'Yes', value: 'Yes' },
        { text: 'No', value: 'No' },
        { text: '-', value: 'null' },
      ],
      onFilter: (value, record) =>
        value === 'null'
          ? record.include_in_project_screener == null
          : record.include_in_project_screener === value,
      sorter: (a, b, sortOrder) =>
        compareStrings(a.include_in_project_screener, b.include_in_project_screener, sortOrder),
      render: (value: 'Yes' | 'No' | null | undefined) => {
        if (value === 'Yes') return <Tag color="green">Yes</Tag>;
        if (value === 'No') return <Tag>No</Tag>;
        return <span>-</span>;
      },
    },
    {
      title: 'Project Type',
      dataIndex: 'project_type',
      key: 'project_type',
      filters: projectTypeFilters,
      onFilter: (value, record) =>
        value === 'null' ? record.project_type == null : record.project_type === value,
      sorter: (a, b, sortOrder) => compareStrings(a.project_type, b.project_type, sortOrder),
      render: (value: string | null | undefined) => value || '—',
    },
    {
      title: 'Market Label Conditions',
      dataIndex: 'market_label_conditions',
      key: 'market_label_conditions',
      width: '20%',
      sorter: (a, b, sortOrder) =>
        compareStrings(a.market_label_conditions, b.market_label_conditions, sortOrder),
      ellipsis: {
        showTitle: false,
      },
      render: (value: string | undefined) => (
        <Tooltip placement="topLeft" title={value}>
          {value || '—'}
        </Tooltip>
      ),
    },
    {
      title: (
        <Space size={4}>
          Registry Methodology
          <Tooltip
            title={
              <div style={{ maxWidth: 260, lineHeight: 1.6 }}>
                These are other methodology names mapped from other registries or Berkeley.
              </div>
            }
          >
            <InfoCircleOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }} />
          </Tooltip>
        </Space>
      ),
      dataIndex: 'registry_methodology',
      key: 'registry_methodology',
      width: '20%',
      sorter: (a, b, sortOrder) =>
        compareStrings(
          a.registry_methodology?.length ? a.registry_methodology.join(', ') : undefined,
          b.registry_methodology?.length ? b.registry_methodology.join(', ') : undefined,
          sortOrder,
        ),
      render: (value: string[] | null | undefined) =>
        value && value.length ? (
          <div style={{ whiteSpace: 'normal', lineHeight: 1.6 }}>
            {value.map((entry, i) => (
              <div key={`${entry}-${i}`}>{entry}</div>
            ))}
          </div>
        ) : (
          '—'
        ),
    },
    {
      title: 'Action',
      key: 'action',
      width: '25%',
      align: 'center',
      render: (_, record) => (
        <Popconfirm
          title="Are you sure you want to delete this?"
          onConfirm={(e) => {
            e?.stopPropagation();
            handleDelete(record.id);
          }}
          onCancel={(e) => e?.stopPropagation()}
          okText="Yes"
          cancelText="No"
        >
          <Button
            type="link"
            danger
            onClick={(e) => e.stopPropagation()}
          >
            Delete
          </Button>
        </Popconfirm>
      ),
    }
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const ghgStandardsData = await getGHGStandards();
        setGHGStandards(ghgStandardsData.response);
        const ghgMethodologiesData = await getGHGMethodologies();
        setGHGMethodologies(ghgMethodologiesData.response);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSearch = (value: string) => {
    setSearchText(value);
  };

  const searchTerms = searchText.trim().toLowerCase().split(/\s+/).filter(Boolean);

  const matchesSearch = (...fields: (string | null | undefined)[]) => {
    if (!searchTerms.length) return true;
    const haystack = fields
      .filter(Boolean)
      .map(field => String(field).toLowerCase().replace(/_/g, ' '))
      .join(' ');
    return searchTerms.every(term => haystack.includes(term));
  };

  const filteredGHGStandards = ghgStandards.filter(standard =>
    matchesSearch(standard.code, standard.full_form, standard.description)
  );

  const filteredGHGMethodologies = ghgMethodologies.filter(methodology =>
    matchesSearch(methodology.display_name, methodology.name, methodology.code)
  );

  const handleDelete = async (id: number) => {
    try {
      setLoading(true);
      await deleteGHGMethodology(id);
      setLoading(false);
      setGHGMethodologies(prev => prev.filter(item => item.id !== id));
      message.success({
        content: 'GHG methodology deleted successfully',
        duration: 4,
        style: { textAlign: 'right' },
      });
    } catch (error: any) {
      setLoading(false);
      // Backend messages for a blocked delete can list several linked categories in one
      // sentence, so use a modal instead of an auto-dismissing toast to give it room to be read.
      Modal.error({
        title: "Can't delete this methodology",
        content: error.message,
      });
    }
  };

  return (
    <div className="table-container">
      <Spin spinning={loading} indicator={<LoadingOutlined style={{ fontSize: 24, color: 'white' }} spin />} fullscreen />
      <Tabs defaultActiveKey={defaultActiveKey}>
        <TabPane tab="Methodologies" key="1">
          <div className="table-controls">
            <div>
              <Input
                prefix={<SearchOutlined />}
                placeholder="Search GHG Methodology"
                value={searchText}
                onChange={e => handleSearch(e.target.value)}
                style={{ marginBottom: '16px', width: '50%' }}
              />
            </div>
            <Link to="/ghg_methodologies/create">
              <Button type="primary" icon={<PlusOutlined />} style={{ marginRight: '8px' }}>Create GHG Methodology</Button>
            </Link>
          </div>
          <Table
            columns={ghgMethodologyColumns}
            dataSource={filteredGHGMethodologies.map(ghgMethodology => ({ ...ghgMethodology, key: ghgMethodology.id }))}
            onRow={(record: GHGMethodology) => ({
              onClick: () => navigate(`/ghg_methodologies/edit/${record.id}`),
              style: { cursor: 'pointer' }
            })}
          />
        </TabPane>
        <TabPane tab="Registry" key="2">
          <div className="table-controls">
            <div>
              <Input
                prefix={<SearchOutlined />}
                placeholder="Search Registry"
                value={searchText}
                onChange={e => handleSearch(e.target.value)}
                style={{ marginBottom: '16px', width: '50%' }}
              />
            </div>
            <Link to="/ghg_standards/create">
              <Button type="primary" icon={<PlusOutlined />} style={{ marginRight: '8px' }}>Create Registry</Button>
            </Link>
          </div>
          <Table
            columns={ghgStandardColumns}
            dataSource={filteredGHGStandards.map(ghgStandard => ({ ...ghgStandard, key: ghgStandard.id }))}
            onRow={(record: GHGStandard) => ({
              onClick: () => navigate(`/ghg_standards/edit/${record.id}`),
              style: { cursor: 'pointer' }
            })}
          />
        </TabPane>
      </Tabs>
    </div>
  );
};

export default ViewGHGMetaComponent;
